import { AsyncState } from 'react-async';
import Address from 'data/Address';
import { VendorCompanyDetailsCredit } from 'data/VendorCompany';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';

// Credit Tracking Modes
export enum CreditTrackingMode {
  OFMP = 'OFMP',
  ERP = 'ERP', 
  UID_REQUIRED = 'UID_REQUIRED',
  OFFLINE = 'OFFLINE'
}


// Connection Status Types
export enum ConnectionStatus {
  CONNECTED = 'connected',
  OFFLINE = 'offline',
  FAILED = 'failed'
}

// Company MDM Details Interface
export interface CompanyMDMDetails {
  apigeeId: string;
  name: string;
  address: string;
  accountType: string;
  creditData: string;
  idStatus: string;
}

// Credit Information Interface
export interface CreditInfo {
  totalCreditLimit: number;
  availableCreditLimit: number;
  utilization: number;
  isReadOnly: boolean;
  lastUpdated?: string;
  companyMdmDetails?: CompanyMDMDetails;
}

// Vendor Company Info Interface
export interface VendorCompanyInfo {
  companyName: string;
  address: Pick<Address, 'addressLine1' | 'addressLine2' | 'town' | 'region' | 'countryIsoCode' | 'postalCode'>;
  accountClassification: string;
  activeIndicator: boolean;
  creditId?: string;
  hasCredit: boolean;
  vendorCompanyId: string;
  id: number;
}

// MDM Company Card Props Interface
export interface MDMCompanyCardProps {
  className?: string;
  mdmId: string;
  isConnected?: boolean;
  isInvalid: boolean;
  setIsInvalid: (value: boolean) => void;
  onApplyMDM: (value: string) => void;
  creditTrackingMode?: CreditTrackingMode;
  creditInfo?: CreditInfo;
  error?: string;
}

// MDM Credit Card Props Interface
export interface MDMCreditCardProps {
  className?: string;
  isConnected: boolean;
  // lastUpdated: string;
  creditInfo: CreditInfo;
  onUpdateCredit: (value: string) => void;
  onTrackManuallyClick?: () => void;
  creditTrackingMode: CreditTrackingMode;
  vendorCompanyDetails: VendorCompanyDetailsCredit
}

// MDM General Tab Props Interface
export interface MDMGeneralTabProps {
  selectedBusinessUnit?: { id?: number; code?: string; name: string; vendorId?: number };
  companyId: string;
  mdmId: string;
  creditId: string;
  isConnected: boolean;
  error?: string;
  creditInfo: CreditInfo;
  vendorCompanyDetails: VendorCompanyInfo;
  creditTrackingMode: CreditTrackingMode;
  isInvalid: boolean;
  setIsInvalid: (value: boolean) => void;
  mdmApplyReq?: AsyncState<any>;
  updateCreditReq?: AsyncState<any>;
  createCreditReq?: AsyncState<any>;
  vendorsList?: Vendor[];
  initialVendor?: Vendor;
  initialBusinessUnit?: BusinessUnit;
  hasActiveSubscriptions?: boolean;
  featureFlags?: FeatureFlag;
}

// Credit Management Hook Return Interface
export interface CreditManagementHookReturn {
  // State
  isLoading: boolean;
  error: string | null;
  
  // Operations
  validateMDMId: (sellerCode: string, buyerCode: string, mdmId: string) => Promise<any>;
  updateCredit: (creditId: string, buyerExternalCode: string, amount: number, updatedBy: string, reserved: number, used: number, allowNegative: boolean, source: any) => Promise<any>;
  switchToOFMP: (creditId: string, buyerExternalCode: string, amount: number, reserved: number, used: number, allowNegative: boolean, updatedBy: string) => Promise<any>;
}

// Address Lines Interface
export interface AddressLines {
  line1: string;
  line2: string;
  line3: string;
}

// Connection Status Configuration Interface
export interface ConnectionStatusConfig {
  status: string;
  backgroundColor: string;
  color: string;
}

// Credit Button Configuration Interface
export interface CreditButtonConfig {
  text: string;
  onClick: () => void;
  variant: 'primary' | 'secondary';
  disabled?: boolean;
}